import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shuffle, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { LogoWithFallback, toolLogoUrls } from "@/components/LogoWithFallback";

const TABS = [
  { id: "popular",    label: "Popular",        sort: "user_score" },
  { id: "new",        label: "New",            sort: "created_at" },
  { id: "trending",   label: "Trending",       sort: "trending"   },
];

/** Bare, flat logo mark — no circular badge, no shadow.
 *  22px matches the "Powered by" model chip size in the header, giving
 *  a consistent tiny-icon rhythm across the whole top strip. */
function ToolIcon({ tool }) {
  const initial = (tool.name || tool.logo_letter || "?").slice(0, 1).toUpperCase();
  const bg = tool.logo_color || "#0B1220";
  return (
    <Link
      to={`/tool/${tool.slug}`}
      title={tool.name}
      data-testid={`popular-tool-${tool.slug}`}
      className="shrink-0 inline-flex items-center justify-center hover:-translate-y-0.5 transition-transform"
    >
      <LogoWithFallback
        urls={toolLogoUrls(tool)}
        letter={initial}
        color={bg}
        size={22}
        bare
        alt={tool.name}
      />
    </Link>
  );
}

function CategoryIcon({ cat }) {
  const initial = (cat.label || "?").slice(0, 1).toUpperCase();
  return (
    <Link
      to={`/?category=${encodeURIComponent(cat.value)}`}
      title={cat.label}
      data-testid={`popular-cat-${cat.value}`}
      className="shrink-0 inline-flex items-center justify-center hover:-translate-y-0.5 transition-transform"
    >
      <span
        className="inline-flex items-center justify-center font-bold text-[12px]"
        style={{ width: 22, height: 22, color: cat.color || "#0B1220" }}
      >
        {initial}
      </span>
    </Link>
  );
}

/**
 * PopularToolsRow — single-line compact discovery strip.
 *
 * One row:  [POPULAR ▼] [shuffle]  ●●●●●●●●●●●…
 *
 * The tab picker collapses to a dropdown trigger, freeing the horizontal
 * space that used to belong to a full row of text tabs + a second row of
 * captioned icons. All caption labels are dropped so we can pack roughly
 * twice as many icons into the same visual height.
 */
export function PopularToolsRow() {
  const [tab, setTab] = useState("popular");
  const [tools, setTools] = useState(null);
  const [seed, setSeed] = useState(0);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const activeTab = useMemo(() => TABS.find(t => t.id === tab), [tab]);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const load = useCallback(async () => {
    if (!activeTab || !activeTab.sort) return; // categories mode uses static data
    setTools(null);
    try {
      const r = await api.get("/tools", { params: { sort: activeTab.sort, per_page: 40 } });
      const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
      // Dedupe by canonical brand: strip trailing version/variant tokens so
      // "Suno" and "Suno v4 Pro" collapse to a single entry. Also fall back
      // to `slug` prefix so we never show the same tool twice in this strip.
      const seen = new Set();
      const deduped = [];
      for (const t of items) {
        const brand = (t.name || "").toLowerCase().split(/[\s\-—–]/)[0];
        const slugRoot = (t.slug || "").split("-")[0];
        const key = brand || slugRoot;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(t);
      }
      const rotated = seed > 0 && deduped.length > 4
        ? [...deduped.slice(seed % deduped.length), ...deduped.slice(0, seed % deduped.length)]
        : deduped;
      setTools(rotated);
    } catch {
      setTools([]);
    }
  }, [activeTab, seed]);

  useEffect(() => { load(); }, [load]);

  const pickTab = (id) => {
    setTab(id);
    setOpen(false);
    if (id === "categories") {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="category-pills"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 40);
    }
  };

  const isCategories = tab === "categories";
  const categoryList = useMemo(
    () => CATEGORIES.filter(c => c.value !== "all").slice(0, 24),
    []
  );

  return (
    <div className="mb-5" data-testid="popular-tools-row">
      <div className="flex items-center gap-3">
        {/* Dropdown trigger */}
        <div ref={menuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
            data-testid="popular-mode-trigger"
            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold text-[#0B1220] hover:text-[#F97316] transition-colors leading-none px-1 py-1 rounded"
          >
            <span>{activeTab?.label || "Popular"}</span>
            <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
          </button>
          {open && (
            <div
              role="listbox"
              className="absolute left-0 top-full mt-1 w-[180px] rounded-lg bg-white border border-[#E7EAF0] shadow-lg z-30 py-1"
              data-testid="popular-mode-menu"
            >
              {TABS.map(t => {
                const active = t.id === tab;
                return (
                  <button
                    key={t.id}
                    role="option"
                    aria-selected={active}
                    onClick={() => pickTab(t.id)}
                    data-testid={`popular-mode-${t.id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[12.5px] transition-colors ${
                      active ? "bg-[#F7F8FA] text-[#0B1220] font-semibold" : "text-[#0B1220] hover:bg-[#F6F7FB]"
                    }`}
                  >
                    <span>{t.label}</span>
                    {active && <Check size={12} className="text-[#0B1220]"/>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Shuffle */}
        {!isCategories && (
          <button
            type="button"
            onClick={() => setSeed(s => s + 3 + Math.floor(Math.random() * 5))}
            title="Shuffle"
            aria-label="Shuffle"
            className="shrink-0 w-6 h-6 rounded-full inline-flex items-center justify-center text-[#5B6478] hover:text-[#0B1220] hover:bg-[#F6F7FB] transition-colors"
            data-testid="popular-shuffle"
          >
            <Shuffle size={11}/>
          </button>
        )}

        {/* Icons — inline on the same row, flat raw logo marks. */}
        <div className="flex items-center gap-2.5 overflow-x-auto scrollbar-hide pr-1 min-w-0" data-testid="popular-icons">
          {isCategories ? (
            categoryList.map(c => <CategoryIcon key={c.value} cat={c}/>)
          ) : tools === null ? (
            [...Array(20)].map((_, i) => (
              <div key={i} className="w-[22px] h-[22px] rounded skeleton shrink-0"/>
            ))
          ) : (
            tools.map(t => <ToolIcon key={t.slug} tool={t}/>)
          )}
        </div>
      </div>
    </div>
  );
}
